/**
 * Common utilities for P2P transfer views (sender and receiver).
 */

import type { AbstractPowerSyncDatabase } from "@powersync/common";
import {
  createSignalingClient,
  type SignalingClient,
  SignalingError,
  type SnapshotDb,
  type TransferErrorKind,
  TransferProtocolError,
} from "@habits/shared";
import { SERVER_BASE_URL } from "@/lib/api";

/**
 * Signaling client against this build's backend. The `/api/p2p/*`
 * endpoints are public (receiver can be a device with no account),
 * so platform fetch without credentials suffices.
 */
export function createP2pSignalingClient(): SignalingClient {
  return createSignalingClient({ baseUrl: SERVER_BASE_URL.replace(/\/$/u, "") });
}

/** Collapses any flow error into a `TransferErrorKind` for the UI. */
export function mapTransferError(error: unknown): TransferErrorKind {
  if (error instanceof SignalingError) {
    return error.kind;
  }
  if (error instanceof TransferProtocolError) {
    return error.reason === "too-large" ? "too-large" : "transfer-failed";
  }
  if (error instanceof Error) {
    if (error.message === "p2p-connect-timeout" || error.message === "p2p-connection-failed") {
      return "connection-failed";
    }
    if (error.message === "p2p-signaling-failed") {
      return "network";
    }
  }
  return "transfer-failed";
}

/**
 * Structural bridge PowerSync → `SnapshotDb` (the minimal surface used by
 * buildSnapshot/importSnapshot) without type assertions.
 */
export function toSnapshotDb(db: AbstractPowerSyncDatabase): SnapshotDb {
  return {
    getAll: async (sql, params) => db.getAll(sql, params),
    execute: async (sql, params) => db.execute(sql, params),
    writeTransaction: async (fn) =>
      db.writeTransaction(async (tx) =>
        fn({
          getAll: async (sql, params) => tx.getAll(sql, params),
          execute: async (sql, params) => tx.execute(sql, params),
        }),
      ),
  };
}
